import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { storage } from '@/composables/useStorage'

const routes = [
  {
    path: '/',
    redirect: '/menu'
  },
  {
    path: '/auth',
    name: 'Auth',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/AuthPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/city',
    name: 'CitySelection',
    component: () => import(/* webpackChunkName: "city" */ '@/views/CitySelectionPage.vue'),
    meta: { requiresAuth: false, showBackButton: true }
  },
  {
    path: '/menu',
    name: 'Menu',
    component: () => import(/* webpackChunkName: "menu" */ '@/views/MenuPage.vue'),
    meta: { 
      requiresAuth: false,
      requiresCity: true,
      showSearchButton: true,
      showFavoritesButton: true
    }
  },
  {
    path: '/category/:id',
    name: 'Category',
    component: () => import(/* webpackChunkName: "menu" */ '@/views/CategoryPage.vue'),
    meta: { requiresAuth: false, requiresCity: true, showBackButton: true }
  },
  {
    path: '/product/:id',
    name: 'Product',
    component: () => import(/* webpackChunkName: "menu" */ '@/views/ProductPage.vue'),
    meta: { requiresAuth: false, requiresCity: true, showBackButton: true }
  },
  {
    path: '/search',
    name: 'Search',
    component: () => import(/* webpackChunkName: "menu" */ '@/views/SearchPage.vue'),
    meta: { requiresAuth: false, requiresCity: true, showBackButton: true }
  },
  {
    path: '/favorites',
    name: 'Favorites',
    component: () => import(/* webpackChunkName: "menu" */ '@/views/FavoritesPage.vue'),
    meta: { requiresAuth: true, showBackButton: true }
  },
  {
    path: '/cart',
    name: 'Cart',
    component: () => import(/* webpackChunkName: "cart" */ '@/views/CartPage.vue'),
    meta: { requiresAuth: false, showBackButton: true }
  },
  {
    path: '/checkout',
    name: 'Checkout',
    component: () => import(/* webpackChunkName: "cart" */ '@/views/CheckoutPage.vue'),
    meta: { requiresAuth: false, showBackButton: true }
  },
  {
    path: '/orders',
    name: 'Orders',
    component: () => import(/* webpackChunkName: "orders" */ '@/views/OrdersPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/order/:id',
    name: 'OrderDetails',
    component: () => import(/* webpackChunkName: "orders" */ '@/views/OrderDetailsPage.vue'),
    meta: { requiresAuth: true, showBackButton: true }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import(/* webpackChunkName: "settings" */ '@/views/SettingsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/page/:slug',
    name: 'PageView',
    component: () => import(/* webpackChunkName: "page" */ '@/views/PageView.vue'),
    meta: { requiresAuth: false, requiresCity: true, showBackButton: true }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Navigation guard для проверки авторизации
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Если маршрут требует авторизации
  if (to.meta.requiresAuth) {
    // Проверяем авторизацию
    if (!authStore.isAuthenticated) {
      // Пытаемся восстановить сессию
      const restored = await authStore.checkAuth()
      if (!restored) {
        next({ name: 'Auth' })
        return
      }
    }
  }
  
  // Если маршрут требует выбора города (но не авторизации)
  if (to.meta.requiresCity) {
    // Проверяем наличие города (из пользователя или localStorage)
    const userCity = authStore.currentUser?.city
    let savedCity = storage.get('selected_city')
    if (!userCity && !savedCity) {
      savedCity = 'Ижевск'
      storage.set('selected_city', 'Ижевск')
    }
    const hasCity = userCity || savedCity
    
    if (!hasCity && to.name !== 'CitySelection') {
      next({ name: 'CitySelection' })
      return
    }
  }
  
  // Если уже авторизован и пытается зайти на страницу авторизации
  if (to.name === 'Auth' && authStore.isAuthenticated) {
    const userCity = authStore.currentUser?.city
    const savedCity = storage.get('selected_city')
    if (userCity || savedCity) {
      next({ name: 'Menu' })
    } else {
      next({ name: 'CitySelection' })
    }
    return
  }
  
  next()
})

export default router
